import React, { useMemo, useState } from "react";
import { AlertTriangle, Building2, Loader2, RefreshCw, Search, UserCheck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

/**
 * "Visible to" picker for a company document.
 *
 * Departments is only offered when the plan includes departments. A document
 * that already targets departments on a plan that has since dropped them keeps
 * that audience (the server still enforces it) — the option is shown locked with
 * an explanation, and the admin can move it to All or Selected.
 */
export default function CompanyDocumentAudienceField({ value, onChange, options, loading, error, onRetry }) {
  const departmentsEnabled = Boolean(options?.departments_enabled);
  const lockedDepartments = !departmentsEnabled && value.mode === "departments";

  const setMode = (mode) => {
    if (mode === value.mode) return;
    onChange({ mode, departments: [], employee_ids: [] });
  };

  const modes = [
    { value: "all", label: "All employees", hint: "Everyone in your company", icon: Users },
    ...(departmentsEnabled || lockedDepartments
      ? [
          {
            value: "departments",
            label: "Departments",
            hint: "Only employees in the departments you choose",
            icon: Building2,
            disabled: lockedDepartments,
          },
        ]
      : []),
    { value: "selected", label: "Selected employees", hint: "Only the employees you choose", icon: UserCheck },
  ];

  return (
    <div className="space-y-2">
      <Label>Visible to</Label>

      <RadioGroup value={value.mode} onValueChange={setMode} className="gap-2">
        {modes.map((m) => {
          const Icon = m.icon;
          const selected = value.mode === m.value;
          const id = `company-doc-audience-${m.value}`;
          return (
            <label
              key={m.value}
              htmlFor={id}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-lg border p-2.5 transition-colors sm:p-3",
                selected ? "border-primary bg-primary/[0.04] dark:bg-primary/10" : "border-border hover:bg-muted/50",
                m.disabled && !selected && "cursor-not-allowed opacity-60",
              )}
            >
              <RadioGroupItem id={id} value={m.value} disabled={m.disabled && !selected} className="mt-0.5" />
              <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", selected ? "text-primary" : "text-muted-foreground")} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">{m.label}</span>
                {/* On phones only the chosen option explains itself, to keep the list short. */}
                <span className={cn("block text-xs text-muted-foreground", !selected && "hidden sm:block")}>
                  {m.hint}
                </span>
              </span>
            </label>
          );
        })}
      </RadioGroup>

      {value.mode !== "all" && (
        <div className="pt-1">
          {loading ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading {value.mode === "departments" ? "departments" : "employees"}...
            </div>
          ) : error ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              <span>{error}</span>
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Retry
              </Button>
            </div>
          ) : lockedDepartments ? (
            <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Your plan no longer includes departments. This document stays limited to{" "}
                <strong>{value.departments.join(", ")}</strong>. Choose another option to change who can see it.
              </span>
            </div>
          ) : value.mode === "departments" ? (
            <SearchableChecklist
              items={(options?.departments || []).map((name) => ({ id: name, label: name }))}
              selected={value.departments}
              onChange={(departments) => onChange({ ...value, departments })}
              noun="department"
              emptyText="No departments are set up yet. Add them in Settings."
            />
          ) : (
            <SearchableChecklist
              items={(options?.employees || []).map((e) => ({
                id: e._id,
                label: e.name,
                sublabel: [e.employee_code, e.department].filter(Boolean).join(" · "),
              }))}
              selected={value.employee_ids}
              onChange={(employee_ids) => onChange({ ...value, employee_ids })}
              noun="employee"
              emptyText="No active employees found."
            />
          )}
        </div>
      )}
    </div>
  );
}

function SearchableChecklist({ items, selected, onChange, noun, emptyText }) {
  const [query, setQuery] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const knownIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  // Saved selections that are no longer offered (e.g. an employee who has left).
  const hiddenCount = selected.filter((id) => !knownIds.has(id)).length;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.label} ${i.sublabel || ""}`.toLowerCase().includes(q));
  }, [items, query]);

  const toggle = (id) =>
    onChange(selectedSet.has(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const selectShown = () => onChange([...new Set([...selected, ...shown.map((i) => i.id)])]);

  if (items.length === 0 && hiddenCount === 0) {
    return <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{emptyText}</p>;
  }

  return (
    <div className="rounded-lg border">
      {items.length > 6 && (
        <div className="relative border-b p-2">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${noun}s...`}
            className="h-9 pl-9"
            aria-label={`Search ${noun}s`}
          />
        </div>
      )}

      <div className="max-h-44 overflow-y-auto p-1 thin-scrollbar sm:max-h-52" role="group" aria-label={`${noun}s`}>
        {shown.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">No {noun}s match "{query}".</p>
        ) : (
          shown.map((item) => {
            const id = `audience-${noun}-${item.id}`;
            return (
              <label
                key={item.id}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60"
              >
                <Checkbox id={id} checked={selectedSet.has(item.id)} onCheckedChange={() => toggle(item.id)} />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-foreground">{item.label}</span>
                  {item.sublabel && (
                    <span className="block truncate text-xs text-muted-foreground">{item.sublabel}</span>
                  )}
                </span>
              </label>
            );
          })
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2 text-xs">
        <span className={cn(selected.length ? "font-medium text-primary" : "text-muted-foreground")}>
          {selected.length} {noun}
          {selected.length === 1 ? "" : "s"} selected
          {hiddenCount > 0 && <span className="text-muted-foreground"> (incl. {hiddenCount} no longer listed)</span>}
        </span>
        <span className="flex gap-3">
          {shown.length > 0 && (
            <button type="button" onClick={selectShown} className="font-medium text-primary hover:underline">
              {query ? "Select matches" : "Select all"}
            </button>
          )}
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])} className="text-muted-foreground hover:text-foreground hover:underline">
              Clear
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
